import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FormField, Input, ZagButton, AppStatusComponent } from '@zag/ui';
import { AuthService } from './auth.service';
import { AuthModeService } from './auth-mode.service';
import { SocialLoginButtonsComponent } from './social-login-buttons/social-login-buttons.component';

/**
 * Custom validator to check if passwords match
 */
function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (!password || !confirmPassword) {
    return null;
  }

  return password.value === confirmPassword.value ? null : { passwordMismatch: true };
}

@Component({
  selector: 'sc-register',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ReactiveFormsModule, TranslatePipe, FormField, Input, ZagButton, SocialLoginButtonsComponent, AppStatusComponent],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.register' | translate }}</span>
        </h2>

        @if (authService.error()) {
          <div class="error-message">
            {{ authService.error() }}
          </div>
        }

        <!-- Configured credential/password registration form -->
        @if (authModeService.emailPasswordEnabled()) {
          <form [formGroup]="registerForm" (ngSubmit)="onSubmit()">
            <div class="form-fields">
              <zag-form-field
                [label]="'auth.name' | translate"
                [error]="registerForm.get('name')?.touched && registerForm.get('name')?.invalid ? ('errors.required' | translate) : ''"
                [required]="true"
                forId="register-name"
              >
                <zag-input
                  id="register-name"
                  formControlName="name"
                  type="text"
                  [placeholder]="'auth.namePlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field
                [label]="credentialLabel() | translate"
                [error]="credentialError() | translate"
                [required]="true"
                forId="register-identifier"
              >
                <zag-input
                  id="register-identifier"
                  formControlName="identifier"
                  [type]="authModeService.usernameEnabled() ? 'text' : 'email'"
                  [placeholder]="credentialPlaceholder() | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field
                [label]="'auth.password' | translate"
                [error]="
                  registerForm.get('password')?.touched && registerForm.get('password')?.invalid
                    ? ('errors.passwordMinLength' | translate)
                    : ''
                "
                [required]="true"
                forId="register-password"
              >
                <zag-input
                  id="register-password"
                  formControlName="password"
                  type="password"
                  [placeholder]="'auth.passwordPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field
                [label]="'auth.confirmPassword' | translate"
                [error]="getConfirmPasswordError() | translate"
                [required]="true"
                forId="register-confirm-password"
              >
                <zag-input
                  id="register-confirm-password"
                  formControlName="confirmPassword"
                  type="password"
                  [placeholder]="'auth.confirmPasswordPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <button zagButton zagType="primary" type="submit" [disabled]="authService.isLoading()" class="submit-button">
                {{ authService.isLoading() ? ('auth.signingUp' | translate) : ('auth.signUp' | translate) }}
              </button>
            </div>
          </form>
        }

        <!-- Divider between email/password and social login -->
        @if (authModeService.emailPasswordEnabled() && authModeService.hasSocialProviders()) {
          <div class="auth-divider">
            <span>{{ 'auth.or' | translate }}</span>
          </div>
        }

        <!-- Social login buttons -->
        @if (authModeService.hasSocialProviders()) {
          <sc-social-login-buttons
            [providers]="authModeService.socialProviders()"
            [disabled]="authService.isLoading()"
            (providerClick)="signUpWithSocial($event)"
          />
        }

        <div class="auth-footer">
          <p>
            {{ 'auth.haveAccount' | translate }}
            <a routerLink="/login" class="link">{{ 'auth.signIn' | translate }}</a>
          </p>
        </div>
      </div>
      <footer class="auth-status-footer">
        <zag-app-status />
      </footer>
    </div>
  `,
  styles: [
    `
      .auth-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: var(--spacing-md);
        height: 100dvh;
        box-sizing: border-box;
        overflow: hidden;
      }

      .auth-status-footer {
        position: absolute;
        bottom: var(--spacing-md);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
        max-width: 400px;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .error-message {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-error-bg);
        border: 1px solid var(--color-error-border);
        border-radius: var(--border-radius-sm);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }

      .social-login-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .social-login-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
      }

      .social-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .auth-divider {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        color: var(--color-neutral-2);
        font-size: var(--font-size-sm);
      }

      .auth-divider::before,
      .auth-divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: var(--border-color-default);
      }

      .auth-footer {
        text-align: center;
        margin-top: var(--spacing-md);
      }

      .link {
        color: var(--color-primary-alt);
        text-decoration: none;
        font-weight: var(--font-weight-medium);
      }

      .link:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class RegisterComponent {
  protected readonly authService = inject(AuthService);
  protected readonly authModeService = inject(AuthModeService);

  readonly registerForm = new FormGroup(
    {
      name: new FormControl('', [Validators.required]),
      identifier: new FormControl('', [Validators.required, control => this.validateCredential(control)]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: passwordsMatchValidator }
  );

  /**
   * Initiate social sign-up via the auth service.
   *
   * For social providers, sign-up and sign-in are the same flow — better-auth
   * auto-creates users on first social login.
   *
   * The callbackURL is an absolute URL so that after the OAuth callback
   * (which happens directly on the API server), better-auth redirects
   * back to the frontend origin.
   */
  signUpWithSocial(providerId: string): void {
    const callbackURL = new URL('/', globalThis.location.origin).href;
    this.authService.signInWithSocial(providerId, callbackURL);
  }

  getConfirmPasswordError(): string {
    const control = this.registerForm.get('confirmPassword');

    if (!control?.touched) {
      return '';
    }

    if (control.hasError('required')) {
      return 'errors.required';
    }

    if (this.registerForm.hasError('passwordMismatch')) {
      return 'errors.passwordMismatch';
    }

    return '';
  }

  async onSubmit(): Promise<void> {
    this.registerForm.controls.identifier.updateValueAndValidity();
    if (this.registerForm.valid) {
      this.authService.clearError();

      const { name, identifier, password } = this.registerForm.value;

      try {
        if (!name || !identifier || !password) {
          return;
        }

        await this.authService.signUp(
          this.authModeService.usernameEnabled() && Validators.email(new FormControl(identifier)) !== null
            ? { name, username: identifier, password }
            : { name, email: identifier, password }
        );
      } catch {
        // Error is exposed by AuthService.
      }
    } else {
      this.registerForm.markAllAsTouched();
    }
  }

  protected credentialLabel(): string {
    return this.authModeService.usernameEnabled() ? 'auth.emailOrUsername' : 'auth.email';
  }

  protected credentialPlaceholder(): string {
    return this.authModeService.usernameEnabled() ? 'auth.emailOrUsernamePlaceholder' : 'auth.emailPlaceholder';
  }

  protected credentialError(): string {
    const control = this.registerForm.controls.identifier;
    if (!control.touched || control.valid) return '';
    return this.authModeService.usernameEnabled() ? 'errors.invalidEmailOrUsername' : 'errors.invalidEmail';
  }

  private validateCredential(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value !== 'string' || value.length === 0) return null;
    if (!this.authModeService.usernameEnabled() || Validators.email(control) === null) return Validators.email(control);

    const policy = this.authModeService.usernamePolicy();
    const minLength = policy?.minLength ?? 5;
    const maxLength = policy?.maxLength ?? 30;
    return /^[a-zA-Z0-9_.]+$/.test(value) && value.length >= minLength && value.length <= maxLength ? null : { username: true };
  }
}
